import type { Mission } from '~/types/game'
import { assetUrl } from '~/utils/assetUrl'

/**
 * 👑 Boss portraits (v1.42, per Javi). Keyed stills in public/bosses/ —
 * shown as the boss's marker on the painted throne room (instead of the
 * old card marker; tap to inspect) and headed for idle/victory/defeat
 * loops once the animated versions are approved. Sources live in
 * art/approved-sources/Bosses/.
 */
// ?v=2 / vessk ?v=4: v4.57's face-off mirror pass (per Javi) — basalt,
// faust, hexa, janus, sandman, sanguis, vessk and yin-and-yang flipped
// IN PLACE (still + idle/victory/defeat; showcases untouched).
// 🔁 v7.58 (per Javi: "make sure the bosses are facing left - in game, in
// their portrait and in the resolution screen"): gorge and umbrax were the
// two left facing right - flipped the same way (still + loops, q82 like the
// v4.90 diet; showcases untouched: a mirror would swap the pedestal's
// element ring). Every other boss faces left or face-on (Myriad and its
// shards, Hexa, the Amalgam, Basalt's crystal, the Yin/Yang halves; Janus
// has a face each way).
// ☁️ v7.84: these maps hold site paths (the art host's bucket keys); the
// functions below hand out the URLs to render, through assetUrl().
// 🌑 v7.74 (per Javi: "i wasnt happy with how his wings came out. So this
// new version has no wings!"): umbrax re-keyed from his new wingless source
// (art/approved-sources/Bosses/Umbrax.png, mirrored to face left) - ?v=3,
// with new loops (STATE_ART_VERSION '5') and showcase (?v=2).
// 🎭 v7.76 (per Javi: a new Janus source - face-on, four element heads above
// his own): re-keyed, NOT mirrored (he stands face-on like Hexa) - ?v=3, new
// loops (STATE_ART_VERSION '3') and showcase (?v=2).
export const BOSS_ART: Record<string, string> = {
  'sandman': '/bosses/sandman.webp?v=2',
  'umbrax': '/bosses/umbrax.webp?v=3',
  'myriad': '/bosses/myriad.webp',
  'myriad-split2a': '/bosses/myriad-split2a.webp',
  'myriad-split2b': '/bosses/myriad-split2b.webp',
  'myriad-split4a': '/bosses/myriad-split4a.webp',
  'myriad-split4b': '/bosses/myriad-split4b.webp',
  'myriad-split4c': '/bosses/myriad-split4c.webp',
  'myriad-split4d': '/bosses/myriad-split4d.webp',
  'vessk': '/bosses/vessk.webp?v=4',
  'hexa': '/bosses/hexa.webp?v=2',
  'gorge': '/bosses/gorge.webp?v=2',
  'janus': '/bosses/janus.webp?v=3',
  'yin': '/bosses/yin.webp',
  'yang': '/bosses/yang.webp',
  'yin-and-yang': '/bosses/yin-and-yang.webp?v=2',
  'dr-vex': '/bosses/dr-vex.webp',
  'dr-vex-amalgam': '/bosses/dr-vex-amalgam.webp',
  'sanguis': '/bosses/sanguis.webp?v=2',
  'faust': '/bosses/faust.webp?v=2',
  'basalt': '/bosses/basalt.webp?v=2',
  'basalt-crystal': '/bosses/basalt-crystal.webp',
}

/** 🏛️ Evil-pedestal showcases (v1.43, per Javi): the boss idling on the
 * dark dais ringed by the element arc (tools/pedestal_composite.py +
 * art/references/evilpedestal.png). Shown by the Reference compendium
 * and the boss cards. */
export const BOSS_SHOWCASE: Record<string, string> = {
  'sandman': '/bosses/sandman-showcase.webp',
  'umbrax': '/bosses/umbrax-showcase.webp?v=2',
  'myriad': '/bosses/myriad-showcase.webp',
  'vessk': '/bosses/vessk-showcase.webp?v=3',
  'hexa': '/bosses/hexa-showcase.webp',
  'gorge': '/bosses/gorge-showcase.webp',
  'janus': '/bosses/janus-showcase.webp?v=2',
  'yin-and-yang': '/bosses/yin-and-yang-showcase.webp',
  'dr-vex': '/bosses/dr-vex-showcase.webp',
  'dr-vex-amalgam': '/bosses/dr-vex-amalgam-showcase.webp',
  'sanguis': '/bosses/sanguis-showcase.webp',
  'faust': '/bosses/faust-showcase.webp',
  'basalt': '/bosses/basalt-showcase.webp',
}

// 🍃 v4.90 asset diet: showcases re-encoded at 512px in place — bust
// the cache without losing any per-boss rev above.
for (const k of Object.keys(BOSS_SHOWCASE)) {
  const url = BOSS_SHOWCASE[k]!
  BOSS_SHOWCASE[k] = `${url}${url.includes('?') ? '&' : '?'}d=1`
}

/** The showcase for a boss SKILL — the named boss's identity (Dr. Vex
 * himself fronts the scientist fight, not his Amalgam). */
export function bossShowcaseForSkill(skill: string | undefined | null): string | null {
  if (!skill) return null
  const id = skill === 'scientist' ? 'dr-vex' : SKILL_ART[skill]
  const url = id ? BOSS_SHOWCASE[id] : undefined
  return url ? assetUrl(url) : null
}

/** The still portrait for a boss SKILL (list rows; the showcase animates
 * only when inspected — same performance rule as the creature codex). */
export function bossPortraitForSkill(skill: string | undefined | null): string | null {
  if (!skill) return null
  const id = skill === 'scientist' ? 'dr-vex' : SKILL_ART[skill]
  const url = id ? BOSS_ART[id] : undefined
  return url ? assetUrl(url) : null
}

/** The bundled VS seal follows the named boss, including Dr. Vex in front of his Amalgam. */
export function bossSealForSkill(skill: string | undefined | null): string | null {
  if (!skill) return null
  const id = skill === 'scientist' ? 'dr-vex' : SKILL_ART[skill]
  return typeof id === 'string' ? `/ui/versus/${id}.webp` : null
}

/** 🎭 v7.56: the boss's own idle loop for the face-off scenes - the one that
 * TALKS (Dr. Vex himself, not his Amalgam), beside the portrait above. */
export function bossIdleForSkill(skill: string | undefined | null): string | null {
  if (!skill) return null
  const id = skill === 'scientist' ? 'dr-vex' : SKILL_ART[skill]
  return id ? bossStateArt(id, 'idle') : null
}

/** 🎨 v7.71 (per Javi: "their colors are dull whereas the creatures colors pop when they speak"): each face-off
 * boss's colour lift while it speaks - [brightness, saturate, contrast] - measured on its idle loop and still
 * (scratchpad color_lift2.py: closest to the creatures' portraits, luminance 0.33 / colourfulness 0.33, with at most
 * ~5% of the loop's pixels clipped). Gorge is already as vivid as the creatures. */
export const BOSS_LIFT: Record<string, readonly [number, number, number]> = {
  'sandman': [1, 1.15, 1],
  // 🌑 v7.74: re-measured on the new wingless art (its gold blades and chest flare clip sooner).
  'umbrax': [1, 1.25, 1],
  'myriad': [1, 1.1, 1],
  'vessk': [1.15, 1.2, 1],
  'hexa': [1.3, 1.5, 1],
  'gorge': [1, 1, 1],
  // 🎭 v7.76: re-measured on the new Janus art.
  'janus': [1, 1.25, 1],
  'yin-and-yang': [1.05, 1.6, 1.08],
  'dr-vex': [1.1, 1.6, 1],
  'sanguis': [1.05, 1.2, 1],
  'faust': [1.1, 1.25, 1],
  'basalt': [1.05, 1.45, 1],
}
/** The face-off speaker's lift as a CSS filter list ('' = none). */
export function bossLiftForSkill(skill: string | undefined | null): string {
  if (!skill) return ''
  const id = skill === 'scientist' ? 'dr-vex' : SKILL_ART[skill]
  const lift = id ? BOSS_LIFT[id] : undefined
  if (!lift) return ''
  const [b, s, c] = lift
  return [b !== 1 && `brightness(${b})`, s !== 1 && `saturate(${s})`, c !== 1 && `contrast(${c})`].filter(Boolean).join(' ')
}

/** 🧪 v7.69 (per Javi): the shape that looms behind the boss in the face-off -
 * Dr. Vex talks, his Amalgam stands behind him. Everyone else stands alone. */
export function bossBehindForSkill(skill: string | undefined | null): string | null {
  const url = skill === 'scientist' ? BOSS_ART['dr-vex-amalgam'] : undefined
  return url ? assetUrl(url) : null
}
/** 🧪 v7.72: the shape behind's idle loop (the Amalgam breathes behind Dr. Vex while reactions play). */
export function bossBehindIdleForSkill(skill: string | undefined | null): string | null {
  return skill === 'scientist' ? bossStateArt('dr-vex-amalgam', 'idle') : null
}

/** Skill → the whole boss's portrait (splits/minions handled below). */
const SKILL_ART: Record<string, string> = {
  knockout: 'sandman',
  eclipse: 'umbrax',
  fracture: 'myriad',
  corrupted: 'vessk',
  cursed: 'hexa',
  unstable: 'gorge',
  feint: 'janus',
  yinyang: 'yin-and-yang',
  scientist: 'dr-vex-amalgam',
  bloodprice: 'sanguis',
  curse: 'faust',
  golem: 'basalt',
}

/**
 * The portrait for a boss-fight mission: the boss itself, a split piece
 * (Myriad's fragments by depth, Yin/Yang halves by name), or a minion
 * (Dr. Vex's meddling / Basalt's crystals). Null = keep the emoji marker.
 */
/** Stable tiny hash so a fragment keeps ITS shard across re-renders. */
function pieceIndex(id: string, count: number): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return h % count
}

export function bossMissionId(mission: Pick<Mission, 'id' | 'tier' | 'bossSkill' | 'fractureDepth' | 'minion' | 'name'>): string | null {
  if (mission.minion === 'crystal') return 'basalt-crystal'
  if (mission.minion === 'scientist') return 'dr-vex'
  if (mission.tier !== 'boss') return null
  const skill = mission.bossSkill
  if (skill === 'fracture' && (mission.fractureDepth ?? 0) > 0) {
    // Javi painted the fragments as one sheet per generation — sliced
    // into individual shards (v1.42): halves a/b, then Cores a-d.
    if ((mission.fractureDepth ?? 1) === 1) {
      return `myriad-split2${'ab'[pieceIndex(mission.id, 2)]}`
    }
    return `myriad-split4${'abcd'[pieceIndex(mission.id, 4)]}`
  }
  if (skill === 'yinyang' && (mission.fractureDepth ?? 0) > 0) {
    // Match the LABEL after the em-dash, not the whole name — every
    // half is called "Yin & Yang — <half>", so a bare includes('yang')
    // matched the parent name and painted BOTH halves as Yang
    // (2026-07-16 review, M1).
    const half = mission.name.split('—').pop()?.trim().toLowerCase() ?? ''
    return half === 'yang' ? 'yang' : 'yin'
  }
  return (skill ? SKILL_ART[skill] : undefined) ?? null
}

export function bossMissionArt(mission: Pick<Mission, 'id' | 'tier' | 'bossSkill' | 'fractureDepth' | 'minion' | 'name'>): string | null {
  const id = bossMissionId(mission)
  return id ? assetUrl(BOSS_ART[id]!) : null
}

/** 🎭 Animated boss states (v1.64, Javi-approved batch): every entry in
 * BOSS_ART has idle/victory/defeat loops beside its still — idle plays
 * on the throne-room marker, the reactions on the resolution card. */
/** 🔄 Cache-bust for assets REPLACED IN PLACE (the act-2 badge lesson,
 * v2.41): bump an id's version when its files change under the same
 * names, or clients keep the cached originals for a long time. */
const STATE_ART_VERSION: Record<string, string> = {
  // '2' = the v4.57 mirror pass; vessk had a prior '3' and moves to '4'.
  'vessk': '5',
  'sandman': '2',
  'hexa': '2',
  'janus': '3', // 🎭 v7.76: all three loops re-rendered from the new source
  'yin-and-yang': '2',
  'sanguis': '2',
  'faust': '2',
  'basalt': '2',
  // '2' = the v7.58 mirror pass (gorge and umbrax, see BOSS_ART).
  // 🪽 v7.69 (per Javi: wings and tails clipped): umbrax's three loops,
  // gorge's victory and vessk's defeat re-rendered clip-free - '3', '3', '5'.
  // 🪽 v7.71 (per Javi: "the last one was wicked cool with the wings"): umbrax's
  // idle unfurls its wings again, clip-free - '4'.
  // 🌑 v7.74 (per Javi: the new wingless source): all three loops re-rendered
  // from it - '5'.
  'gorge': '3',
  'umbrax': '5',
}

export function bossStateArt(id: string, state: 'idle' | 'victory' | 'defeat'): string | null {
  if (!BOSS_ART[id]) return null
  // 🧊 defeat rev 'f2' (v4.15): loop-patched to freeze on the last
  // frame; composed with any per-boss version so neither buster is lost.
  // 🍃 'd1' (v4.90): the asset diet re-encoded every state loop at
  // 384px in place — composed the same way.
  const v = [STATE_ART_VERSION[id], state === 'defeat' ? 'f2' : null, 'd1'].filter(Boolean).join('-')
  return assetUrl(`/bosses/${id}-${state}.webp${v ? `?v=${v}` : ''}`)
}

export function bossMissionState(mission: Pick<Mission, 'id' | 'tier' | 'bossSkill' | 'fractureDepth' | 'minion' | 'name'>, state: 'idle' | 'victory' | 'defeat'): string | null {
  const id = bossMissionId(mission)
  return id ? bossStateArt(id, state) : null
}
